import React, { createContext, useContext, useState, useEffect } from "react";
import * as signalR from "@microsoft/signalr";
import { resourceAPIURL } from "../config";

interface ActiveLeads {
  [leadId: string]: string[];
}

interface ActiveMessages {
  [messageId: string]: string[];
}

interface SignalRContextType {
  connection: signalR.HubConnection | null;
  activeLeads: ActiveLeads;
  activeMessages: ActiveMessages;
}

const SignalRContext = createContext<SignalRContextType | null>(null);

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null
  );
  const [activeLeads, setActiveLeads] = useState<ActiveLeads>({});
  const [activeMessages, setActiveMessages] = useState<ActiveMessages>({});

  useEffect(() => {
    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${resourceAPIURL}/leadHub`, { withCredentials: false })
      .withAutomaticReconnect()
      .build();

    newConnection
      .start()
      .then(() => {
        setConnection(newConnection);
        console.log("Hub connected!");

        // When we connect to the hub, we get a initial state of active leads.
        newConnection.on("InitialSync", (activeLeads: ActiveLeads) => {
          console.log("initial sync", activeLeads);
          setActiveLeads(activeLeads ?? {});
        });

        // When a lead is opened, update activeLeads with the lead's userId.
        newConnection.on("RecordOpened", (leadId: string, userId: string) => {
          setActiveLeads((prev) => ({
            ...prev,
            [leadId]: prev[leadId] ? [...prev[leadId], userId] : [userId],
          }));
        });

        // When a lead is closed, remove it from activeLeads.
        newConnection.on("RecordClosed", (leadId: string, userId: string) => {
          setActiveLeads((prev) => {
            const updatedUsers =
              prev[leadId]?.filter((id) => id !== userId) || [];

            if (updatedUsers.length === 0) {
              // Remove the key if there are no users left
              const { [leadId]: _, ...newState } = prev;
              return newState;
            }

            return {
              ...prev,
              [leadId]: updatedUsers,
            };
          });
        });
      })
      .catch((err) => console.error("SignalR connection error: ", err));

    // Separate hub for eBay message presence — lets staff see who is
    // currently reading/replying to a message so two people don't send
    // duplicate responses. Mirrors the /leadHub contract so the
    // server-side implementation can be a copy with a different scope.
    const messageConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${resourceAPIURL}/messageHub`, { withCredentials: false })
      .withAutomaticReconnect()
      .build();

    messageConnection
      .start()
      .then(() => {
        messageConnection.on(
          "InitialSync",
          (active: ActiveMessages) => {
            setActiveMessages(active ?? {});
          }
        );

        messageConnection.on(
          "MessageOpened",
          (messageId: string, userId: string) => {
            setActiveMessages((prev) => ({
              ...prev,
              [messageId]: prev[messageId]
                ? Array.from(new Set([...prev[messageId], userId]))
                : [userId],
            }));
          }
        );

        messageConnection.on(
          "MessageClosed",
          (messageId: string, userId: string) => {
            setActiveMessages((prev) => {
              const updated =
                prev[messageId]?.filter((id) => id !== userId) || [];
              if (updated.length === 0) {
                const { [messageId]: _, ...rest } = prev;
                return rest;
              }
              return { ...prev, [messageId]: updated };
            });
          }
        );
      })
      .catch((err) =>
        // Swallow — the /messageHub endpoint may not exist yet in every
        // environment. Presence will simply stay empty.
        console.warn("Message hub connection error: ", err)
      );

    return () => {
      newConnection.stop();
      messageConnection.stop();
    };
  }, []);

  return (
    <SignalRContext.Provider value={{ connection, activeLeads, activeMessages }}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = () => {
  const context = useContext(SignalRContext);

  if (!context) {
    throw new Error("useSignalR must be used within a SignalRProvider");
  }

  return context;
};
